import { Button, buttonVariants } from "@gigpro/ui/Button";
import { DialogPrimitive } from "@gigpro/ui/Dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@gigpro/ui/DropdownMenu";
import { ArrowRight, Close, Location, QwickLogo } from "@gigpro/ui/Icons";
import { Separator } from "@gigpro/ui/Separator";
import { Sheet, SheetContent } from "@gigpro/ui/Sheet";
import { cn } from "@gigpro/ui/tailwind/cn";
import { useBreakpoint } from "@gigpro/ui/utils/useBreakpoint";
import { Link } from "@tanstack/react-router";
import { LocationBar } from "domains/locations/containers/LocationBar";
import { OrganizationSwitcher } from "domains/orgs/containers/OrganizationSwitcher";
import { UserProfileContainer } from "domains/orgs/containers/UserProfile";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type { BaseLayoutSlots } from "layouts/BaseLayout";
import { NavigationBar } from "layouts/BaseLayout/TopNavigation";
import type { NavItem, NavItemButton, NavItemLink } from "layouts/BaseLayout/types";
import { useNavLinkProps } from "layouts/BaseLayout/utils";
import { DropdownNavItem } from "layouts/MainNavigation/DropdownNavItem";
import { SuperAdminButton } from "layouts/MainNavigation/SuperAdminButton";
import { PanelLeft, ShieldCheck } from "lucide-react";
import type React from "react";
import { drawerAtom } from "services/atoms";
import { SidebarNavItem, SidebarTooltip, sidebarRowClassName } from "./SidebarNavItem";

const sidebarCollapsedAtom = atomWithStorage("qwick-sidebar-collapsed", false, undefined, { getOnInit: true });

type SidebarNavItems = {
  main: Array<NavItem>;
  sidebar: Array<SidebarGroup>;
  admin: Array<NavItem>;
  topBar: Array<NavItem>;
  inbox: NavItemLink;
  agentChat: NavItem | null;
};

export type SidebarGroup = Array<NavItem | "admin">;

const groupKey = (item: SidebarGroup[number]) => (item === "admin" || item.type === "divider" ? "admin" : item.title);

type AdminMenuProps = { items: Array<NavItem>; collapsed: boolean; onNavigate?: () => void };

function AdminMenu({ items, collapsed, onNavigate }: Readonly<AdminMenuProps>) {
  return (
    <div className={cn("flex items-center", collapsed && "flex-col gap-1")}>
      <DropdownMenu>
        <SidebarTooltip collapsed={collapsed} label="Admin">
          <DropdownMenuTrigger aria-label={collapsed ? "Admin" : undefined} className={sidebarRowClassName(collapsed)}>
            <ShieldCheck />
            {!collapsed && (
              <>
                Admin
                <ArrowRight className="ml-auto" />
              </>
            )}
          </DropdownMenuTrigger>
        </SidebarTooltip>
        <DropdownMenuContent side="right" align="end" className="w-56 p-0">
          {items.filter(isLinkOrButton).map((item) => (
            <DropdownNavItem key={item.title} item={item} onClick={onNavigate} />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <SuperAdminButton className={cn(collapsed && "ml-0 rounded-sm px-2")} />
    </div>
  );
}

type SidebarNavigationProps = {
  title: string;
  hideDefaultHeader?: boolean;
  slots?: Pick<BaseLayoutSlots, "topNavigation" | "mobileHeader">;
  navItems: SidebarNavItems;
  modeSwitcher?: React.ReactNode;
};

const iconButtonClassName = "rounded-full p-1.5 text-white hover:bg-white/20";

const isLinkOrButton = (item: NavItem): item is NavItemLink | NavItemButton =>
  item.type === "link" || item.type === "button";

type SidebarPanelProps = {
  navItems: SidebarNavItems;
  modeSwitcher?: React.ReactNode;
  collapsed: boolean;
  toggleLabel: string;
  toggleIcon: React.ReactNode;
  omit: Array<NavItem>;
  onToggle: () => void;
  onNavigate?: () => void;
};

function SidebarPanel({
  navItems,
  modeSwitcher,
  collapsed,
  toggleLabel,
  toggleIcon,
  omit,
  onToggle,
  onNavigate,
}: Readonly<SidebarPanelProps>) {
  const navLinkProps = useNavLinkProps();
  const { sidebar, admin, inbox } = navItems;
  const [top = [], ...bottom] = sidebar
    .map((group) => group.filter((item) => (item === "admin" ? admin.length > 0 : !omit.includes(item))))
    .filter((group) => group.length > 0);
  const renderItem = (item: SidebarGroup[number]) => {
    if (item === "admin") return <AdminMenu key="admin" items={admin} collapsed={collapsed} onNavigate={onNavigate} />;
    if (!isLinkOrButton(item)) return null;
    return <SidebarNavItem key={item.title} item={item} collapsed={collapsed} onClick={onNavigate} />;
  };

  return (
    <>
      <div className={cn("flex items-center gap-1", collapsed ? "flex-col" : "justify-between pl-2")}>
        {!collapsed && <QwickLogo className="h-6 shrink-0" />}
        <div className={cn("flex items-center gap-1", collapsed && "flex-col-reverse")}>
          {!omit.includes(inbox) && (
            <SidebarTooltip collapsed={collapsed} label={inbox.title}>
              <Link
                {...navLinkProps(inbox)}
                onClick={onNavigate}
                aria-label={inbox.title}
                className={cn(buttonVariants({ icon: true }), iconButtonClassName)}
              >
                {inbox.icon}
              </Link>
            </SidebarTooltip>
          )}
          <Button icon aria-label={toggleLabel} onClick={onToggle} className={iconButtonClassName}>
            {toggleIcon}
          </Button>
        </div>
      </div>

      {collapsed && <Separator className="bg-white/15" />}

      {modeSwitcher && (
        <div
          className={cn(
            "[&>button]:w-full [&>button]:py-2",
            collapsed && "[&>button]:justify-center [&>button>span]:hidden [&>button>svg:last-child]:hidden",
          )}
        >
          {modeSwitcher}
        </div>
      )}

      <nav aria-label="Main" className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto">
        <div className="flex flex-col gap-0.5">{top.map(renderItem)}</div>
        <div className="mt-auto flex flex-col gap-4 pt-4">
          {bottom.map((group) => (
            <div key={group.map(groupKey).join()} className="flex flex-col gap-0.5">
              {group.map(renderItem)}
            </div>
          ))}
        </div>
      </nav>

      {collapsed ? (
        <OrganizationSwitcher asChild>
          <Button icon aria-label="Switch location" className={cn("mx-auto", iconButtonClassName)}>
            <Location className="size-4" />
          </Button>
        </OrganizationSwitcher>
      ) : (
        <OrganizationSwitcher className="w-full">
          <UserProfileContainer size="small" />
        </OrganizationSwitcher>
      )}
    </>
  );
}

export function SidebarNavigation({
  title,
  hideDefaultHeader,
  slots,
  navItems,
  modeSwitcher,
}: Readonly<SidebarNavigationProps>) {
  const isDesktop = useBreakpoint("md");
  const [collapsed, setCollapsed] = useAtom(sidebarCollapsedAtom);
  const [drawerOpen, setDrawerOpen] = useAtom(drawerAtom);
  const closeDrawer = () => setDrawerOpen(false);

  if (isDesktop) {
    const width = collapsed ? "w-12" : "w-56";
    // Fixed, with an in-flow placeholder: a sticky aside scrolls away with layouts capped at 100dvh.
    return (
      <>
        <div aria-hidden className={cn("shrink-0 transition-all duration-200", width)} />
        <aside
          data-sidebar-nav
          style={{ viewTransitionName: "main-navigation" }}
          className={cn(
            "fixed inset-y-0 left-0 z-30 flex flex-col gap-3 bg-black p-2 text-white transition-all duration-200",
            width,
          )}
        >
          <SidebarPanel
            navItems={navItems}
            modeSwitcher={modeSwitcher}
            collapsed={collapsed}
            toggleLabel={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            toggleIcon={<PanelLeft className="size-4" />}
            omit={[]}
            onToggle={() => setCollapsed((prev) => !prev)}
          />
        </aside>
      </>
    );
  }
  return (
    <div data-sidebar-nav className="contents">
      {slots?.topNavigation ?? <NavigationBar title={title} topBar={navItems.topBar} drawerItems={navItems.main} />}
      {!hideDefaultHeader && <LocationBar />}
      {slots?.mobileHeader}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent
          side="left"
          className="flex w-60 flex-col gap-3 bg-black p-2 text-white data-[state=closed]:duration-200 data-[state=open]:duration-300"
        >
          <DialogPrimitive.Title className="sr-only">Navigation Menu</DialogPrimitive.Title>
          <SidebarPanel
            navItems={navItems}
            modeSwitcher={modeSwitcher}
            collapsed={false}
            toggleLabel="Close menu"
            toggleIcon={<Close className="size-4" />}
            omit={navItems.topBar}
            onToggle={closeDrawer}
            onNavigate={closeDrawer}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
